import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import AddBuyerAddress from '@components/AddBuyerAddress';
import ImageUpload from '@components/ImageUpload';
import LoaderComponent from '@components/LoaderComponent';
import { toastError, toastSuccess } from '@components/Toast';

import ApiClient from '@lib/apiClient';

const EditBrand = () => {
	const { id } = useParams();
	const [loading, setLoading] = useState(false);
	const [brandInfo, setBrandInfo] = useState({});
	const [isAddAddress, setIsAddAddress] = useState(false);
	const [brandLocations, setBrandLocations] = useState([]);
	const [isEdit, setIsEdit] = useState(false);
	const [isEditLocation, setIsEditLocation] = useState(false);
	const [editedLocationInfo, setEditedLocationInfo] = useState(null);

	const [errors, setErrors] = useState({
		brandNameError: ''
	});

	const navigate = useNavigate();

	const getBrandInfo = useCallback(async () => {
		setLoading(true);
		await ApiClient.GET('getBrandInfo', id)
			.then(({ data }) => {
				setLoading(false);
				setBrandInfo(data);
			})
			.catch(({ response }) => {
				setLoading(false);
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				} else {
					toastError('Something went wrong! Please try again.');
				}
			});
	}, [id]);

	const getBrandLocations = useCallback(async () => {
		setLoading(true);
		await ApiClient.GET('getBrandLocations', id)
			.then(({ data }) => {
				setLoading(false);
				setBrandLocations(data);
			})
			.catch(({ response }) => {
				setLoading(false);
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				} else {
					toastError('Something went wrong! Please try again.');
				}
			});
	}, [id]);

	useEffect(() => {
		document.title = 'Edit brand - Nitex - The easiest clothing manufacturing software';
		getBrandInfo();
		getBrandLocations();
	}, []);

	const onBrandChange = (e) => {
		e.preventDefault();
		setErrors({
			...errors,
			brandNameError: ''
		});
		setBrandInfo({
			...brandInfo,
			[e.target.id]: e.target.value
		});
	};

	const onAddBuyerAddress = () => {
		setIsAddAddress(!isAddAddress);
		getBrandLocations();
	};
	const onEditLocation = (location) => {
		setIsEditLocation(true);
		setEditedLocationInfo(location);
	};

	const onCloseLocation = () => {
		setIsEditLocation(false);
		setEditedLocationInfo(null);
		getBrandLocations();
	};

	const onUpdate = async () => {
		if (brandInfo.name === '') {
			setErrors({
				...errors,
				brandNameError: 'Brand name is required'
			});
			return;
		}

		let body = brandInfo;
		setLoading(true);
		await ApiClient.PUT('updateBrandInfo', body)
			.then(({ data }) => {
				setLoading(false);
				if (data && data.success) {
					navigate('/brands/list');
					setIsEdit(false);
					toastSuccess(data.message);
				}
			})
			.catch(({ response }) => {
				setLoading(false);
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				} else {
					toastError('Request was unsuccessful.');
				}
			});
	};

	const onUploadImage = (image) => {
		setBrandInfo({
			...brandInfo,
			logoDocumentDTO: image
		});
	};

	const onCancel = () => {
		setIsEdit(false);
		getBrandInfo();
	};

	const onMakeDefault = async (locationId) => {
		setLoading(true);
		await ApiClient.POST('makeLocationDefault', {}, `${brandInfo.id}/${locationId}`)
			.then(({ data }) => {
				setLoading(false);
				if (data && data.success) {
					toastSuccess(data.message);
					getBrandLocations();
				}
			})
			.catch(({ response }) => {
				setLoading(false);
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				} else {
					toastError('Request was unsuccessful.');
				}
			});
	};

	return (
		<LoaderComponent loading={loading}>
			<div className="brand-container">
				<div className="form-container new-brand">
					<div className="form-header pb-3">
						<p className="semibold-16">Edit brand</p>
					</div>
					{!isEdit ? (
						<div className="container px-0">
							<div className="row form-row">
								<div className="col-md-12">
									<h2 className="semibold-16 form-title">Basic info</h2>
								</div>
								<div className="col-md-6">
									<span className="brands-logo">
										<img src={brandInfo?.brandLogoDocResponse?.docUrl} alt />
									</span>
									<h3 className="brand-name semibold-20 gray_dark_01 my-3">
										{brandInfo.name}
									</h3>
									<p className="semibold-14">{brandInfo?.name}</p>
									<p className="regular-14 gray_dark_01">{brandInfo?.email}</p>
									<p className="regular-14 gray_dark_01">{brandInfo?.phone}</p>
									<p className="regular-14 gray_dark_01">{brandInfo?.website}</p>
								</div>
								<div className="col-md-6 text-right">
									<button className="button text size32" onClick={() => setIsEdit(true)}>
										Edit
									</button>
								</div>
							</div>
						</div>
					) : (
						<div className="container px-0">
							<div className="row form-row">
								<div className="col-md-12">
									<h2 className="semibold-16 form-title">Brand info</h2>
								</div>
								<div className="col-md-12">
									<div className="form-group">
										<div className="upload-design-image mb-4 mb-sm-0">
											{brandInfo ? (
												<>
													<img
														className="brand-logo"
														src={
															brandInfo?.logoDocumentDTO?.base64Str
																? brandInfo?.logoDocumentDTO?.base64Str
																: brandInfo?.brandLogoDocResponse?.docUrl
														}
														alt=""
													/>
													<ImageUpload
														onUploadImage={onUploadImage}
														docType={'ACCESSORIES_DESIGN'}
														type={'image'}
														isEditable={true}
													/>
												</>
											) : (
												<ImageUpload
													onUploadImage={onUploadImage}
													docType={'ACCESSORIES_DESIGN'}
													type={'image'}
												/>
											)}
										</div>

										{/* <div className="upload-design-image mb-4 mb-sm-0">
											{brandInfo.brandLogoDocResponse?.docUrl ? (
												<div className="edit-image-box">
													<img
														className="border-0 brand-image"
														src={addImageSuffix(
															brandInfo?.brandLogoDocResponse.docUrl,
															'_xicon'
														)}
														alt=""
													/>
													<img
														className="close-icon-image"
														src={CLOSE_ICON}
														alt=""
														onClick={onCancel}
													/>
												</div>
											) : brandInfo.logoDocumentDTO ? (
												<div className="edit-image-box">
													<img
														className="border-0 brand-image"
														src={addImageSuffix(
															brandInfo?.logoDocumentDTO.base64Str,
															'_xicon'
														)}
														alt=""
													/>
													<img
														className="close-icon-image"
														src={CLOSE_ICON}
														alt=""
														onClick={onCancel}
													/>
												</div>
											) : (
												<>
													<label
														htmlFor="brand-logo-upload"
														className="drag-upload brand-upload"
													>
														<img src={`${process.env.PUBLIC_URL}/icons/upload-sm.svg`} alt="upload" />
														<span className="primary_bright_01">Upload logo</span>
													</label>

													<input
														type="file"
														id="drag-upload"
														className="file-upload"
														name="logo"
														onChange={(e) =>
															onFileUpload(e, 'ACCESSORIES_DESIGN')
														}
													/>
												</>
											)}
										</div> */}
									</div>
								</div>
								<div className="col-md-12">
									<div className="form-group">
										<label htmlFor="bname">Brand name*</label>
										<input
											type="text"
											autoComplete="off"
											placeholder="Type your role name"
											className="bg-gray-light border-0"
											name="name"
											id="name"
											value={brandInfo.name}
											onChange={onBrandChange}
										/>
										{errors.brandNameError ? (
											<p className="error">{errors.brandNameError}</p>
										) : (
											<></>
										)}
									</div>
								</div>
								<div className="col-md-12">
									<div className="form-group">
										<label htmlFor="legalName">Brand legal name</label>
										<input
											type="text"
											autoComplete="off"
											id="legalName"
											value={brandInfo.legalName}
											onChange={onBrandChange}
										/>
									</div>
								</div>
								<div className="col-md-6 pr-10">
									<div className="form-group">
										<label htmlFor="email">Email</label>
										<input
											type="email"
											autoComplete="off"
											id="email"
											value={brandInfo.email}
											onChange={onBrandChange}
										/>
									</div>
								</div>
								<div className="col-md-6 pl-10">
									<div className="form-group">
										<label htmlFor="phone">Phone</label>
										<input
											type="number"
											autoComplete="off"
											id="phone"
											value={brandInfo.phone}
											onChange={onBrandChange}
										/>
									</div>
								</div>
								<div className="col-md-12">
									<div className="form-group">
										<label htmlFor="website">website</label>
										<input
											type="text"
											autoComplete="off"
											id="website"
											value={brandInfo.website}
											onChange={onBrandChange}
										/>
									</div>
								</div>
							</div>
							<div className="row">
								<div className="col-md-12 text-right">
									<button className="button outline size32" onClick={() => onCancel()}>
										Cancel
									</button>
									<button className="button size32 ml-2" onClick={() => onUpdate()}>
										Update
									</button>
								</div>
							</div>
						</div>
					)}
				</div>

				<div className="brand-addresses">
					<div className="common-box">
						<div className="common-box-header">
							<div className="left-half">
								<h3 className="semibold-16 mb-0">Buyer address</h3>
							</div>
							<div className="right-half text-right" onClick={() => onAddBuyerAddress()}>
								<button className="button text size32">
									<span className>Add</span>
									<img src={`${process.env.PUBLIC_URL}/icons/add-round.svg`} alt="add" />
								</button>
							</div>
						</div>
						<div className="common-box-body">
							{brandLocations?.map((item) => (
								<div
									className="full-content  content-with-button d-flex justify-content-between"
									key={item.id}
								>
									<div className="single-content-box">
										<div className="content-heading location-heading mb-2">
											<span className="semibold-14 mb-0">{item.title}</span>
											<img
												src={`${process.env.PUBLIC_URL}/icons/edit-supplier.svg`}
												alt="edit"
												onClick={() => onEditLocation(item)}
											/>
										</div>
										<div className="info-details">
											<p className="regular-14 gray_dark_01">{item.address}</p>
											<p className="regular-14 gray_dark_01">{item.address2}</p>
											<p className="regular-14 gray_dark_01">{item.city}</p>
											<p className="regular-14 gray_dark_01">{item.state}</p>
										</div>
									</div>
									{item.isDefault ? (
										<div className="make-default-btn text-right">
											<button className="button secondary size32">Default</button>
										</div>
									) : (
										<div className="make-default-btn text-right">
											<button
												className="button outline size32 make-default"
												onClick={() => onMakeDefault(item.id)}
											>
												Make default
											</button>
										</div>
									)}
								</div>
							))}
						</div>
					</div>
				</div>
			</div>
			{isEditLocation && (
				<AddBuyerAddress
					details={{ ...brandInfo, brandId: brandInfo.id }}
					geDetailsInfo={getBrandInfo}
					editedLocationInfo={editedLocationInfo}
					isEditLocation={isEditLocation}
					onCloseLocation={onCloseLocation}
				/>
			)}
			{isAddAddress && (
				<AddBuyerAddress
					onAddBuyerAddress={onAddBuyerAddress}
					details={{ ...brandInfo, brandId: brandInfo.id }}
					geDetailsInfo={getBrandInfo}
				/>
			)}
		</LoaderComponent>
	);
};

export default EditBrand;
